import { useAtomValue } from "jotai";
import type { NextPage } from "next";
import { useEffect } from "react";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { AppFooter } from "scmp-app/components/app-footer";
import { GamesLoading } from "scmp-app/components/games/loading";
import { OtherLoadingDuration } from "scmp-app/components/games/loading/consts";
import { useLoadingTimer } from "scmp-app/components/games/loading/hooks";
import { GameType } from "scmp-app/components/games/types";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { useGameIframe, useMiniSudokuSeo } from "scmp-app/pages/games/hooks";
import { GamePageSchema } from "scmp-app/pages/games/schema";
import { globalCss, IframeContainer, IframeWrapper } from "scmp-app/pages/games/styles";

const MiniSudokuPage: NextPage = () => {
  const { iframeId } = useGameIframe({ path: "games/mini-sudoku" });

  const isLoading = useLoadingTimer(OtherLoadingDuration);
  const adZone = `game/minisudoko_main`;
  const seo = useMiniSudokuSeo();

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      autoRefreshOptions: {
        intervalRefreshOptions: {
          isDisable: false,
        },
        scrollBackRefreshOptions: {
          isDisable: true,
        },
      },
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      autoRefreshOptions: {
        intervalRefreshOptions: {
          isDisable: false,
        },
        scrollBackRefreshOptions: {
          isDisable: true,
        },
      },
      sizes: [
        [300, 100],
        [320, 100],
        [300, 50],
        [320, 50],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
  });

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  return (
    <>
      <GamePageSchema />
      <PageSeo {...seo} />
      <IframeContainer $isLoading={isLoading}>
        <IframeWrapper id={iframeId} />
        <GamesLoading
          duration={OtherLoadingDuration}
          isLoading={isLoading}
          theme={GameType.MiniSudoku}
          useGif={true}
        />
      </IframeContainer>
      {!isLoading && <AppFooter variant="games" />}
    </>
  );
};

const pageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 12,
      tabletUp: 0,
    },
  },
  pageConfiguration: {
    responsivePadding: {
      tabletUp: 24,
    },
  },
};

MiniSudokuPage.getInitialProps = () => ({
  ...pageProps,
  extraGlobalCss: globalCss(GameType.MiniSudoku),
});

export default MiniSudokuPage;
